// Section 17: allocation arithmetic shared by the ration policies (content may not import
// content, so the building blocks live here). Shares are fractions of need, 0..1.
import type { Allocation, AllocationInput, RationPolicyDef } from './registry';
import type { PersonId, ReadContext } from './types';

export interface FillState {
  shares: Record<PersonId, number>;
  remaining: number;
}

type Demand = Readonly<Record<PersonId, number>>;

function need(demand: Demand, id: PersonId): number {
  return demand[id] ?? 0;
}

/** Step 4: pins are honoured first, in rank order, as far as stock allows. */
export function applyPins(
  order: readonly PersonId[],
  demand: Demand,
  available: number,
  pinned: Readonly<Record<PersonId, number>>,
): FillState {
  const shares: Record<PersonId, number> = {};
  let remaining = available;
  for (const id of order) {
    const pin = pinned[id];
    if (pin === undefined) continue;
    const d = need(demand, id);
    const give = d > 0 ? Math.min(pin * d, remaining) : 0;
    shares[id] = d > 0 ? give / d : 1;
    remaining -= give;
  }
  return { shares, remaining: Math.max(0, remaining) };
}

/** Everyone in `ids` rises to the same share, at most `cap`, as far as stock allows. */
export function equalFill(
  ids: readonly PersonId[],
  demand: Demand,
  fill: FillState,
  cap = 1,
): void {
  let total = 0;
  for (const id of ids) total += need(demand, id);
  const share = total > 0 ? Math.min(cap, fill.remaining / total) : cap;
  for (const id of ids) {
    fill.shares[id] = share;
    fill.remaining -= share * need(demand, id);
  }
  fill.remaining = Math.max(0, fill.remaining);
}

/**
 * Groups are filled in order from their current share up to 1; the group that straddles the
 * boundary gets equal partial top-ups (Section 17 "Priority order").
 */
export function priorityFill(
  groups: readonly (readonly PersonId[])[],
  demand: Demand,
  fill: FillState,
): void {
  for (const group of groups) {
    let gap = 0;
    for (const id of group) gap += (1 - (fill.shares[id] ?? 0)) * need(demand, id);
    if (gap <= 0) {
      for (const id of group) fill.shares[id] ??= 1;
      continue;
    }
    const t = Math.min(1, fill.remaining / gap);
    for (const id of group) {
      const current = fill.shares[id] ?? 0;
      fill.shares[id] = current + (1 - current) * t;
    }
    fill.remaining = Math.max(0, fill.remaining - gap * t);
  }
}

export function flattenRanked(ranked: readonly (readonly PersonId[])[]): PersonId[] {
  return ranked.flat();
}

/** Floating-point noise allowed before a policy counts as overdrawing the stock. */
const OVERDRAW_TOLERANCE = 1e-9;

/**
 * Section 17: run one ration policy and enforce the engine's contract whatever the policy does:
 * everyone in `ranked` gets a share in 0..1 (1 when they have no demand), and the shares never
 * allocate more than `available`. Pure. Any resource can use it (food, water, warmth here;
 * medicine in the health phase): build `ranked` with the priority groups, then call this.
 */
export function allocateWith(
  def: RationPolicyDef,
  ctx: ReadContext,
  input: AllocationInput,
): Allocation {
  const raw = def.allocate(ctx, input);
  const order = flattenRanked(input.ranked);
  const shares: Record<PersonId, number> = {};
  let given = 0;
  for (const id of order) {
    const d = need(input.demand, id);
    const share = d > 0 ? Math.min(1, Math.max(0, raw.shares[id] ?? 0)) : 1;
    shares[id] = share;
    given += share * d;
  }
  const available = Math.max(0, input.available);
  if (given > available + OVERDRAW_TOLERANCE && given > 0) {
    // Defensive: a policy that overdraws is scaled back to the stock (never happens for the
    // four built-in policies; the property tests check that).
    const scale = available / given;
    for (const id of order) if (need(input.demand, id) > 0) shares[id] = (shares[id] ?? 0) * scale;
  }
  return { shares, order: raw.order.length === order.length ? raw.order : order };
}
